import anime from 'animejs';
import BaseComponent from 'foundation/base-component';

const DURATION = 300;
const EASING = 'linear';

export default class AccordionComponent extends BaseComponent {
    constructor(...args) {
        super(...args);
        this._isOpened = this.element.classList.contains('is-opened') || false;
        this._isAnimating = false;
        this.isExperienceEditor = false;

        this.onChangeSubscribers = [];

        this.onClickTrigger = this.onClickTrigger.bind(this);
        this.onKeydownTrigger = this.onKeydownTrigger.bind(this);
        this.onAnimationComplete = this.onAnimationComplete.bind(this);
        this.init();
    }

    static getRequiredRefs() {
        return ['trigger', 'content'];
    }

    init() {
        setContentToState(this.refs.content, this._isOpened);

        if (!Array.isArray(this.refs.trigger)) {
            this.refs.trigger = [this.refs.trigger];
        }

        this.refs.trigger.forEach((trigger) => {
            trigger.setAttribute('aria-expanded', this._isOpened.toString());
        });
        this.refs.content.setAttribute('aria-hidden', this._isOpened.toString());

        this.addListeners();
    }

    addListeners() {
        this.refs.trigger.forEach((trigger) => {
            this.addListener(trigger, 'click', this.onClickTrigger);
            this.addListener(trigger, 'keydown', this.onKeydownTrigger);
        });
    }

    onClickTrigger(event) {
        if (typeof event !== 'undefined' &&
            typeof event.preventDefault === 'function'
        ) {
            event.preventDefault();
        }

        if (this._isAnimating) return;

        if (this._isOpened) {
            this.collapse();
        } else {
            this.expand();
        }
    }

    onKeydownTrigger(event) {
        const keyCode = event.which;

        switch (keyCode) {
            case 13:
            case 32:
                if (!this.isExperienceEditor) {
                    this.onClickTrigger(event);
                }
                break;
            default:
                break;
        }
    }

    onAnimationComplete() {
        this._isAnimating = false;
        this._isOpened = !this._isOpened;

        this.onChangeSubscribers.forEach((cb) => {
            cb(this._isOpened);
        });
    }

    expand() {
        this._isAnimating = true;
        this.refs.trigger.forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'true');
        });
        this.element.classList.add('is-opened');
        expand(this.refs.content, this.onAnimationComplete);
    }

    collapse() {
        this._isAnimating = true;
        this.refs.trigger.forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'false');
        });
        this.element.classList.remove('is-opened');
        collapse(this.refs.content, this.onAnimationComplete);
    }

    onChange(cb) {
        this.onChangeSubscribers.push(cb);

        return () => {
            this.onChangeSubscribers = this.onChangeSubscribers.filter((item) => item !== cb);
        };
    }
}

export function setContentToState(content, isExpanded) {
    if (isExpanded) {
        content.style.overflow = 'visible';
        content.style.display = 'block';
        content.style.maxHeight = 'none';
    } else {
        content.style.maxHeight = 0;
        content.style.overflow = 'hidden';
        content.style.display = 'none';
    }
}

export function collapse(content, onComplete = null, duration = DURATION, easing = EASING) {
    content.style.overflow = 'hidden';
    content.style.maxHeight = `${content.scrollHeight}px`;

    anime({
        targets: content,
        maxHeight: [content.scrollHeight, 0],
        duration,
        easing,
        complete: () => {
            setContentToState(content, false);
            content.style.overflow = 'hidden';
            content.style.display = 'none';
            content.style.maxHeight = '0';

            if (onComplete) {
                onComplete();
            }
        }
    });
}

export function expand(content, onComplete = null, duration = DURATION, easing = EASING) {
    content.style.display = 'block';

    anime({
        targets: content,
        maxHeight: [0, content.scrollHeight],
        duration,
        easing,
        complete: () => {
            setContentToState(content, true);

            if (onComplete) {
                onComplete();
            }
        }
    });
}
